/** @jsxImportSource @emotion/react */
import {
  createStyles,
  Input,
  ListItem,
  Setters,
  Errors,
  Switch,
  Typography,
} from '@quickbit/qb-design-system';
import { useNavigate, useParams } from 'react-router-dom';
import { usePlatformApi } from 'api/usePlatformApi';
import { Divider } from 'components';
import { Platform } from 'tabs/types';
import { createPercentageFee, createFixedFee } from 'utils';

interface Props {
  formFields: Platform;
  formErrors: Errors<Platform>;
  formSetters: Setters<Platform>;
  onSuccess: (message: string) => void;
  editable: boolean;
}

export const PlatformForm = ({
  formFields,
  formErrors,
  formSetters,
  onSuccess,
  editable,
}: Props) => {
  const { platformId } = useParams();
  const navigate = useNavigate();
  const isNewPlatform = platformId === 'new';
  const styles = useStyles();
  const { deletePlatform } = usePlatformApi();

  const { isPending: deletePlatformLoading, mutate: deletePlatformFn } =
    deletePlatform(() => {
      onSuccess('Platform deleted');
      void navigate('/platforms');
    });

  return (
    <>
      <form css={[styles.container, styles.inputWrapper]}>
        {!isNewPlatform && (
          <>
            <Input
              label="Platform ID"
              value={formFields.uuid || ''}
              onChange={() => null}
              disabled
            />
          </>
        )}

        <Input
          label="Name"
          value={formFields.name}
          disabled={!editable}
          onChange={(e) => formSetters.name.set(e.target.value)}
          helperText={
            formErrors.name ?? 'This is the display name of the platform.'
          }
          error={!!formErrors.name}
        />

        <Typography variant="bodyBold">Default Fees</Typography>
        <Input
          label="Default Pay fee"
          type="number"
          value={formFields.defaultPayFee.value.toString()}
          endAdornment="%"
          disabled={!editable}
          onChange={(e) =>
            formSetters.defaultPayFee.value.set(
              createPercentageFee(e.target.value).value
            )
          }
          error={!!formErrors.defaultPayFee.value}
          helperText={formErrors.defaultPayFee.value}
        />
        <Input
          label="Default Onramp fee"
          type="number"
          value={formFields.defaultOnrampFee.value.toString()}
          endAdornment="%"
          disabled={!editable}
          onChange={(e) =>
            formSetters.defaultOnrampFee.value.set(
              createPercentageFee(e.target.value).value
            )
          }
          error={!!formErrors.defaultOnrampFee.value}
          helperText={formErrors.defaultOnrampFee.value}
        />
        <Input
          label="Default Processing fee"
          type="number"
          value={formFields.defaultProcessingFee.value.toString()}
          endAdornment="%"
          disabled={!editable}
          onChange={(e) =>
            formSetters.defaultProcessingFee.value.set(
              createPercentageFee(e.target.value).value
            )
          }
          error={!!formErrors.defaultProcessingFee.value}
          helperText={formErrors.defaultProcessingFee.value}
        />
        <Input
          label="Default Gateway fee"
          type="number"
          value={formFields.defaultGatewayFee.value.toString()}
          endAdornment="EUR"
          disabled={!editable}
          onChange={(e) =>
            formSetters.defaultGatewayFee.value.set(
              createFixedFee(e.target.value).value
            )
          }
          error={!!formErrors.defaultGatewayFee.value}
          helperText={formErrors.defaultGatewayFee.value}
        />
        <Input
          label="Default Settlement fee"
          type="number"
          value={formFields.defaultSettlementFee.value.toString()}
          endAdornment="EUR"
          disabled={!editable}
          onChange={(e) =>
            formSetters.defaultSettlementFee.value.set(
              createFixedFee(e.target.value).value
            )
          }
          error={!!formErrors.defaultSettlementFee.value}
          helperText={formErrors.defaultSettlementFee.value}
        />
        <ListItem
          title="Enable default Customer fee"
          css={[styles.listItemBorder, !editable && styles.listItemDisabled]}
          action={
            <Switch
              checked={formFields.defaultCustomerFee !== null}
              onChange={(checked) =>
                formSetters.defaultCustomerFee.set(
                  checked ? { type: 'PERCENTAGE', value: 0 } : null
                )
              }
              disabled={!editable}
            />
          }
          disabled={!editable}
        />
        {formFields.defaultCustomerFee && (
          <Input
            label="Default Customer fee"
            type="number"
            endAdornment="%"
            value={formFields.defaultCustomerFee.value.toString()}
            disabled={!editable}
            onChange={(e) =>
              formSetters.defaultCustomerFee.value?.set(
                createPercentageFee(e.target.value).value
              )
            }
            error={!!formErrors.defaultCustomerFee?.value}
            helperText={formErrors.defaultCustomerFee?.value}
          />
        )}
        <Typography variant="bodyBold">Default CW Fees</Typography>
        <Input
          label="Default CW receive external fee"
          type="number"
          value={formFields.defaultCorporateWalletReceiveExternalFee.value.toString()}
          endAdornment="%"
          disabled={!editable}
          onChange={(e) =>
            formSetters.defaultCorporateWalletReceiveExternalFee.value.set(
              createPercentageFee(e.target.value).value
            )
          }
          error={!!formErrors.defaultCorporateWalletReceiveExternalFee.value}
          helperText={formErrors.defaultCorporateWalletReceiveExternalFee.value}
        />
        <Input
          label="Default CW receive external fee minimum applicable amount"
          type="number"
          value={
            formFields.defaultCorporateWalletReceiveExternalFeeMinimumApplicableAmount
          }
          endAdornment="EUR"
          disabled={!editable}
          onChange={(e) =>
            formSetters.defaultCorporateWalletReceiveExternalFeeMinimumApplicableAmount.set(
              e.target.value
            )
          }
          error={
            !!formErrors.defaultCorporateWalletReceiveExternalFeeMinimumApplicableAmount
          }
          helperText={
            formErrors.defaultCorporateWalletReceiveExternalFeeMinimumApplicableAmount
          }
        />
        <Input
          label="Default CW send internal fee"
          type="number"
          value={formFields.defaultCorporateWalletSendInternalFee.value.toString()}
          endAdornment="%"
          disabled={!editable}
          onChange={(e) =>
            formSetters.defaultCorporateWalletSendInternalFee.value.set(
              createPercentageFee(e.target.value).value
            )
          }
          error={!!formErrors.defaultCorporateWalletSendInternalFee.value}
          helperText={formErrors.defaultCorporateWalletSendInternalFee.value}
        />
        <Input
          label="Default CW send external fee"
          type="number"
          value={formFields.defaultCorporateWalletSendExternalFee.value.toString()}
          endAdornment="%"
          disabled={!editable}
          onChange={(e) =>
            formSetters.defaultCorporateWalletSendExternalFee.value.set(
              createPercentageFee(e.target.value).value
            )
          }
          error={!!formErrors.defaultCorporateWalletSendExternalFee.value}
          helperText={formErrors.defaultCorporateWalletSendExternalFee.value}
        />

        <ListItem
          title="Default Allow Pay orders"
          css={[styles.listItemBorder, !editable && styles.listItemDisabled]}
          action={
            <Switch
              checked={formFields.defaultAllowPayOrders}
              onChange={(checked) =>
                formSetters.defaultAllowPayOrders.set(checked)
              }
              disabled={!editable}
            />
          }
          disabled={!editable}
        />

        <ListItem
          title="Default Allow Onramp flow orders"
          css={[styles.listItemBorder, !editable && styles.listItemDisabled]}
          action={
            <Switch
              checked={formFields.defaultAllowOnrampOrders}
              onChange={(checked) =>
                formSetters.defaultAllowOnrampOrders.set(checked)
              }
              disabled={!editable}
            />
          }
          disabled={!editable}
        />

        <ListItem
          title="Default Allow Processing orders"
          css={[styles.listItemBorder, !editable && styles.listItemDisabled]}
          action={
            <Switch
              checked={formFields.defaultAllowProcessingOrders}
              onChange={(checked) =>
                formSetters.defaultAllowProcessingOrders.set(checked)
              }
              disabled={!editable}
            />
          }
          disabled={!editable}
        />

        <ListItem
          title="Default Can Access Credit Card Info"
          css={[styles.listItemBorder, !editable && styles.listItemDisabled]}
          action={
            <Switch
              checked={formFields.defaultCanAccessCreditCardInfo}
              onChange={(checked) =>
                formSetters.defaultCanAccessCreditCardInfo.set(checked)
              }
              disabled={!editable}
            />
          }
          disabled={!editable}
        />

        <ListItem
          title="Default Can Access Personal Information"
          css={[styles.listItemBorder, !editable && styles.listItemDisabled]}
          action={
            <Switch
              checked={formFields.defaultCanAccessPersonalInformation}
              onChange={(checked) =>
                formSetters.defaultCanAccessPersonalInformation.set(checked)
              }
              disabled={!editable}
            />
          }
          disabled={!editable}
        />

        <div>
          <Typography variant="bodyBold">Default order cancellation</Typography>
          <Input
            label="Cancel after"
            type="number"
            value={formFields.defaultMinutesToCancelOrderAfter.toString()}
            onChange={(e) => {
              if (e.target.value) {
                const value = Number(e.target.value);
                if (value < 0) return;
                formSetters.defaultMinutesToCancelOrderAfter.set(
                  parseInt(e.target.value)
                );
              } else {
                formSetters.defaultMinutesToCancelOrderAfter.set(0);
              }
            }}
            endAdornment="minutes"
            error={!!formErrors.defaultMinutesToCancelOrderAfter}
            helperText={formErrors.defaultMinutesToCancelOrderAfter}
          />
          <ListItem
            title="Cancel orders on error"
            css={[styles.listItemBorder, !editable && styles.listItemDisabled]}
            action={
              <Switch
                checked={formFields.defaultCancelOrderOnError}
                onChange={(checked) =>
                  formSetters.defaultCancelOrderOnError.set(checked)
                }
                disabled={!editable}
              />
            }
            disabled={!editable}
          />
        </div>

        <div>
          <Typography variant="bodyBold">Default order callbacks</Typography>
          <ListItem
            title="Retry order callbacks"
            css={[styles.listItemBorder, !editable && styles.listItemDisabled]}
            action={
              <Switch
                checked={formFields.defaultRetryOrderCallbacks}
                onChange={(checked) =>
                  formSetters.defaultRetryOrderCallbacks.set(checked)
                }
                disabled={!editable}
              />
            }
            disabled={!editable}
          />
        </div>

        {!isNewPlatform && formFields.deletedAt && (
          <Input
            label="Deleted At"
            value={formFields.deletedAt.toLocaleString() || ''}
            onChange={() => null}
            disabled
          />
        )}

        {!isNewPlatform && (
          <>
            <Divider />
            <ListItem
              title="Remove platform"
              css={[
                styles.listItemBorder,
                !editable && styles.listItemDisabled,
              ]}
              icon={deletePlatformLoading ? 'spinner' : 'trash'}
              onClick={() => deletePlatformFn(formFields.uuid ?? '')}
              disabled={!editable}
            />
          </>
        )}
      </form>
    </>
  );
};

const useStyles = createStyles(({ spacing, shape, palette }) => ({
  container: {
    background: palette.white,
    borderRadius: shape.borderRadius,
  },
  inputWrapper: {
    marginBottom: 'auto',
    padding: spacing.xxs,
    display: 'grid',
    gridTemplateColumns: '1fr',
    gap: spacing.xxs,
    border: `1px solid ${palette.grey.x100}`,
  },
  listItemBorder: {
    borderRadius: spacing.xxxs,
    border: `1px solid ${palette.grey.x200}`,
    ':hover': {
      borderColor: palette.grey.x600,
    },
  },
  listItemDisabled: {
    ':hover': {
      borderColor: 'inherit',
    },
  },
}));
